import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ROLES } from "../../utils/constants";
import { AllOrders, DrxPage, FutureOrders, Home, MedicineMaster, OrderDetails, Payments, PrxPage, Quotations, Users, Refunds, Carts } from "../../pages";
import UserDetails from "../../pages/user-details";
import EditOrder from "../../pages/edit-orders";
import EditQuotation from "../../pages/edit-quotations";
import EditCart from "../../pages/edit-cart";
import CreateQuotation from "../../pages/create-quotation";
import BatchPage from "../../pages/medicine-master/batch-page";
import { ConfirmDigitalRx, ConfirmPayment, InventoryCheck, OcsFile, PackagingnDispenser, ProcessingOrder, QualifiedLead, Shipped } from "../../pages/track-orders";
import { GLP1Dashboard, SelfCheckoutDashboard, CartReviewDashboard } from "../../pages";
import UploadPRX from "../../pages/track-orders/upload-prx";
import RequestCallbacks from "../../pages/new-request-callback";
import { DigitizeOrders } from "../../pages/digitize";
import Delivery from "../../pages/delivery";
import Marketing from "../../pages/marketing";
import Support from "../../pages/support";
import ActionNeeded from "../../pages/action-needed";
import Reports from "../../pages/reports";
import { PartnerOrders } from "../../pages/partner-orders";
import Welcome from "../../pages/welcome";
import OrderTrackingSheet from "../../pages/order-tracking-sheet";

const LOGIN = {
    LOGHOME: "/login",
    REGISTER: "/register",
};
const RouteConstants = {

    HOME: "/home",
    WELCOME: "/welcome",
    USERDETAILS: "/user-details/:id",
    // RECORDS LAYOUT
    EDIT_ORDER: "/order/edit/:orderId",
    EDIT_QUOTATION: "/quotation/edit/:quotationId",
    CARTS: "/carts",
    EDIT_CART: "/cart/edit/:cartId",
    USERS: "/users",
    USER_PAGE: "/user/:id",
    Medicine_Master: "/medicine-master",
    CURRENTORDERS: "/current-orders",
    FUTUREORDERS: "/future-orders",
    PASTORDERS: "/past-orders",
    ALLORDERS: "/all-orders",
    ORDER_TRACKING_SHEET: "/order-tracking-sheet",
    PARTNER_ORDERS: "/partner-orders",
    ALLREFUNDS: "/all-refunds",
    QUOTATIONS: "/quotations",
    CREATE_QUOTATION: "/quotation/create",
    PAYMENTS: "/payments",
    ORDERDETAILS: "/order-details/:id",
    BatchDetails: "/item-details/:id",

    // STATUS LAYOUT
    STATUS: "/status",

    QUALIFIED_LEAD: "qualified-lead",
    INVENTORY_CHECK: "inventory-check",
    CONFIRM_DIGITAL_RX: "confirm-digital-rx",
    CONFIRM_PAYMENT: "confirm-payment",
    OCS_FILE: "ocs-file",
    PACKAGING_AND_DISPENSER: "packaging-and-dispenser",
    PROCESSING_ORDER: "processing-order",
    SHIPPED: "shipped",
    UPLOAD_PRX: "upload-prx",

    // CALENDER LAYOUT
    CALENDER: "/calender",

    // TRANSACTIONS
    TRANSACTIONS: "/transactions",

    // INVOICES
    INVOICES: "/invoices",

    PRXPAGE: "/prx/:orderId",
    DRXPAGE: "/drx/:orderId",
    DRXPAGE_QUOTATION: "/drx/:orderId/quotation",

    //REQUEST CALLBACK
    REQUEST_CALLBACK: "/request-callbacks",


    //DIGITIZE Orders
    DIGITIZE_ORDERS: "/digitize-orders",

    DELIVERY: "/delivery",
    MARKETING: "/marketing",
    SUPPORT: "/support",
    ACTION_NEEDED: "/action-needed",
    GLP1_DASHBOARD: "/glp1-dashboard",
    SELF_CHECKOUT_DASHBOARD: "/self-checkout-dashboard",
    // Cart Review now lives under Carts; the old path redirects (see CART_REVIEW_LEGACY)
    CART_REVIEW: "/carts/review",
    CART_REVIEW_LEGACY: "/cart-review",
    REPORTS: "/reports",
};

// Keeps old /cart-review bookmarks and shared links (incl. ?cartId=) working
function CartReviewRedirect() {
    const { search } = useLocation();
    return <Navigate to={{ pathname: RouteConstants.CART_REVIEW, search }} replace />;
}

// Who sees the Order Tracking Sheet: everyone who works the ops sheet today -- ops,
// pharmacy, sales (support tiers), logistics and finance. Shared with the sidebar.
export const ORDER_TRACKING_SHEET_ROLES = [
    ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.PHARMACIST, ROLES.SUPER_PHARMACIST,
    ROLES.SUPPORT, ROLES.SUPER_SUPPORT, ROLES.OPS_SUPPORT, ROLES.SUPER_OPS_SUPPORT,
    ROLES.LOGISTICS, ROLES.SUPER_LOGISTICS, ROLES.FINANCE, ROLES.SUPER_FINANCE,
];

const ROUTER_ROLES_MAP = {
    // The only entry registered for EVERY role, deliberately. Routes here are
    // registered by omission (src/navigation/routes.jsx) -- a role absent from an
    // entry's list doesn't get a guarded page, it gets NoPageFound. KAM, DOCTOR and
    // PARTNER appear in no other entry at all, so without this they have no loadable
    // URL anywhere in the app. HOME is deliberately NOT widened to cover them: it
    // renders admin revenue and cohort charts.
    WELCOME: {
        roles: Object.values(ROLES),
        path: RouteConstants.WELCOME,
        element: <Welcome />
    },
    HOME: {
        roles: [ROLES.ADMIN, ROLES.SUPER_ADMIN],
        path: RouteConstants.HOME,
        element: <Home />
    },
    USERDETAILS: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN],
        path: RouteConstants.USERDETAILS,
        element: <UserDetails />
    },
    EDIT_ORDER: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPPORT, ROLES.SUPER_SUPPORT, ROLES.OPS_SUPPORT, ROLES.SUPER_OPS_SUPPORT, ROLES.SUPER_PHARMACIST],
        path: RouteConstants.EDIT_ORDER,
        element: <EditOrder />
    },
    EDIT_QUOTATION: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPPORT, ROLES.OPS_SUPPORT, ROLES.SUPER_OPS_SUPPORT, ROLES.SUPER_PHARMACIST],
        path: RouteConstants.EDIT_QUOTATION,
        element: <EditQuotation />
    },
    USERS: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPPORT],
        path: RouteConstants.USERS,
        element: <Users />
    },
    USER_PAGE: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPPORT],
        path: RouteConstants.USER_PAGE,
        element: <UserDetails />
    },
    Medicine_Master: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT],
        path: RouteConstants.Medicine_Master,
        element: <MedicineMaster />
    },
    FUTUREORDERS: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPPORT],
        path: RouteConstants.FUTUREORDERS,
        element: <FutureOrders />
    },
    DRXPAGE: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPPORT, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.OPS_SUPPORT, ROLES.SUPER_OPS_SUPPORT, ROLES.SUPER_PHARMACIST],
        path: RouteConstants.DRXPAGE,
        element: <DrxPage />
    },
    DRXPAGE_QUOTATION: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPPORT, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.OPS_SUPPORT, ROLES.SUPER_OPS_SUPPORT, ROLES.SUPER_PHARMACIST],
        path: RouteConstants.DRXPAGE_QUOTATION,
        element: <DrxPage />
    },
    ALLORDERS: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPPORT, ROLES.SUPER_PHARMACIST],
        path: RouteConstants.ALLORDERS,
        element: <AllOrders />
    },
    // Read-only view over pillup-api GET /api/ops/tracking-sheet/*; keep this list in step
    // with that route's TRACKING_SHEET_ROLES (routes/api/ops/trackingSheet.js) and with
    // the sidebar entry, which gates on the same roles.
    ORDER_TRACKING_SHEET: {
        roles: ORDER_TRACKING_SHEET_ROLES,
        path: RouteConstants.ORDER_TRACKING_SHEET,
        element: <OrderTrackingSheet />
    },
    PARTNER_ORDERS: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPPORT],
        path: RouteConstants.PARTNER_ORDERS,
        element: <PartnerOrders />
    },
    QUOTATIONS: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPPORT],
        path: RouteConstants.QUOTATIONS,
        element: <Quotations />
    },
    CREATE_QUOTATION: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT],
        path: RouteConstants.CREATE_QUOTATION,
        element: <CreateQuotation />
    },
    PAYMENTS: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPPORT, ROLES.SUPER_FINANCE],
        path: RouteConstants.PAYMENTS,
        element: <Payments />
    },
    ORDERDETAILS: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPPORT, ROLES.OPS_SUPPORT, ROLES.SUPER_OPS_SUPPORT, ROLES.SUPER_PHARMACIST],
        path: RouteConstants.ORDERDETAILS,
        element: <OrderDetails />
    },
    BatchDetails: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT],
        path: RouteConstants.BatchDetails,
        element: <BatchPage />
    },
    QUALIFIED_LEAD: {
        roles: [ROLES.ADMIN, ROLES.SUPPORT, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT],
        path: RouteConstants.QUALIFIED_LEAD,
        element: <QualifiedLead />
    },
    INVENTORY_CHECK: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT],
        path: RouteConstants.INVENTORY_CHECK,
        element: <InventoryCheck />
    },
    CONFIRM_DIGITAL_RX: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT],
        path: RouteConstants.CONFIRM_DIGITAL_RX,
        element: <ConfirmDigitalRx />
    },
    CONFIRM_PAYMENT: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT],
        path: RouteConstants.CONFIRM_PAYMENT,
        element: <ConfirmPayment />
    },
    OCS_FILE: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN],
        path: RouteConstants.OCS_FILE,
        element: <OcsFile />
    },
    PACKAGING_AND_DISPENSER: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT],
        path: RouteConstants.PACKAGING_AND_DISPENSER,
        element: <PackagingnDispenser />
    },
    PROCESSING_ORDER: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT],
        path: RouteConstants.PROCESSING_ORDER,
        element: <ProcessingOrder />
    },
    SHIPPED: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPPORT, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT],
        path: RouteConstants.SHIPPED,
        element: <Shipped />
    },
    UPLOAD_PRX: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT],
        path: RouteConstants.UPLOAD_PRX,
        element: <UploadPRX />
    },
    PRXPAGE: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT],
        path: RouteConstants.PRXPAGE,
        element: <PrxPage />
    },
    REQUEST_CALLBACK: {
        roles: [ROLES.ADMIN, ROLES.SUPPORT, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.MARKETING, ROLES.OPS_SUPPORT, ROLES.SUPER_OPS_SUPPORT],
        path: RouteConstants.REQUEST_CALLBACK,
        element: <RequestCallbacks />
    },
    DIGITIZE_ORDERS: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT],
        path: RouteConstants.DIGITIZE_ORDERS,
        element: <DigitizeOrders />
    },
    DELIVERY: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPPORT, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPER_LOGISTICS],
        path: RouteConstants.DELIVERY,
        element: <Delivery />
    },
    MARKETING: {
        roles: [ROLES.ADMIN, ROLES.MARKETING, ROLES.SUPER_ADMIN],
        path: RouteConstants.MARKETING,
        element: <Marketing />
    },
    SUPPORT: {
        roles: [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT],
        path: RouteConstants.SUPPORT,
        element: <Support />
    },
    ALLREFUNDS: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPPORT],
        path: RouteConstants.ALLREFUNDS,
        element: <Refunds />
    },
    ACTION_NEEDED: {
        roles: [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.PHARMACIST, ROLES.SUPPORT, ROLES.SUPER_SUPPORT],
        path: RouteConstants.ACTION_NEEDED,
        element: <ActionNeeded />
    },
    GLP1_DASHBOARD: {
        roles: [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SUPPORT, ROLES.SUPER_SUPPORT, ROLES.PHARMACIST],
        path: RouteConstants.GLP1_DASHBOARD,
        element: <GLP1Dashboard />
    },
    SELF_CHECKOUT_DASHBOARD: {
        roles: [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SUPPORT, ROLES.SUPER_SUPPORT, ROLES.PHARMACIST],
        path: RouteConstants.SELF_CHECKOUT_DASHBOARD,
        element: <SelfCheckoutDashboard />
    },
    CART_REVIEW: {
        roles: [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SUPPORT, ROLES.SUPER_SUPPORT, ROLES.PHARMACIST],
        path: RouteConstants.CART_REVIEW,
        element: <CartReviewDashboard />
    },
    CART_REVIEW_LEGACY: {
        roles: [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SUPPORT, ROLES.SUPER_SUPPORT, ROLES.PHARMACIST],
        path: RouteConstants.CART_REVIEW_LEGACY,
        element: <CartReviewRedirect />
    },
    CARTS: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPPORT],
        path: RouteConstants.CARTS,
        element: <Carts />
    },
    EDIT_CART: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPPORT],
        path: RouteConstants.EDIT_CART,
        element: <EditCart />
    },
    REPORTS: {
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN],
        path: RouteConstants.REPORTS,
        element: <Reports />
    },
}

export { RouteConstants, LOGIN, ROUTER_ROLES_MAP };
